import { DomainError } from '@glory/kernel';

import { type IsoDate, shiftWorkingDays, type WorkCalendar } from './days.ts';
import type { TaskStatus } from './task.ts';

/** Связь «после»: `taskId` начинается после `afterTaskId` (раздел 5.2). */
export interface TaskLink {
  readonly taskId: string;
  readonly afterTaskId: string;
}

/** Задача на шкале: даты и статус (закрытые не сдвигаются). */
export interface DatedTask {
  readonly id: string;
  readonly startsOn: IsoDate | null;
  readonly endsOn: IsoDate | null;
  readonly status: TaskStatus;
}

export interface DateChange {
  readonly id: string;
  readonly startsOn: IsoDate;
  readonly endsOn: IsoDate;
}

/** Сдвиг незакрытой задачи с датами целиком; закрытая, без дат или не изменившаяся — `null`. */
function shifted(task: DatedTask, delta: number, calendar: WorkCalendar): DateChange | null {
  if (task.status === 'done' || task.startsOn === null || task.endsOn === null) return null;
  const startsOn = shiftWorkingDays(task.startsOn, delta, calendar);
  const endsOn = shiftWorkingDays(task.endsOn, delta, calendar);
  return startsOn === task.startsOn && endsOn === task.endsOn ? null : { id: task.id, startsOn, endsOn };
}

/**
 * Новая связь «`taskId` после `afterTaskId`» замкнёт круг: связь задачи с собой или `afterTaskId` уже
 * (прямо или через цепочку) ждёт `taskId`.
 */
export function wouldCycle(links: readonly TaskLink[], taskId: string, afterTaskId: string): boolean {
  if (taskId === afterTaskId) return true;
  // Круг будет, если от `afterTaskId` по связям «после» назад можно дойти до `taskId`.
  const waitsFor = new Map<string, string[]>();
  for (const link of links)
    waitsFor.set(link.taskId, [...(waitsFor.get(link.taskId) ?? []), link.afterTaskId]);
  const seen = new Set<string>();
  const stack = [afterTaskId];
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined) break;
    if (current === taskId) return true;
    if (seen.has(current)) continue;
    seen.add(current);
    stack.push(...(waitsFor.get(current) ?? []));
  }
  return false;
}

/**
 * Перетаскивание или растягивание задачи на Ганте: начало сдвигается на `startDelta` рабочих дней,
 * конец — на `endDelta` (каждая дата — `shiftWorkingDays`). Все зависимые по связям «после» (прямо и через
 * цепочку, каждая один раз), кроме закрытых (`done`) и задач без дат, сдвигаются на `endDelta` целиком —
 * и начало, и конец. Результат — только изменившиеся задачи; сама задача, если изменилась, — первой.
 * Начало позже конца — `DomainError('projects.dates_invalid')`; у задачи без дат — тоже.
 */
export function moveTask(
  tasks: readonly DatedTask[],
  links: readonly TaskLink[],
  id: string,
  change: { readonly startDelta: number; readonly endDelta: number },
  calendar: WorkCalendar,
): DateChange[] {
  const byId = new Map(tasks.map((task) => [task.id, task]));
  const target = byId.get(id);
  const from = target?.startsOn ?? null;
  const to = target?.endsOn ?? null;
  if (from === null || to === null) throw new DomainError('projects.dates_invalid');
  const startsOn = shiftWorkingDays(from, change.startDelta, calendar);
  const endsOn = shiftWorkingDays(to, change.endDelta, calendar);
  if (startsOn > endsOn) throw new DomainError('projects.dates_invalid');
  const changes: DateChange[] = startsOn === from && endsOn === to ? [] : [{ id, startsOn, endsOn }];
  if (change.endDelta === 0) return changes;
  const dependents = new Map<string, string[]>();
  for (const link of links)
    dependents.set(link.afterTaskId, [...(dependents.get(link.afterTaskId) ?? []), link.taskId]);
  const seen = new Set([id]);
  const queue = [...(dependents.get(id) ?? [])];
  while (queue.length > 0) {
    const next = queue.shift();
    if (next === undefined) break;
    if (seen.has(next)) continue;
    seen.add(next);
    queue.push(...(dependents.get(next) ?? []));
    const task = byId.get(next);
    if (task === undefined) continue;
    const moved = shifted(task, change.endDelta, calendar);
    if (moved !== null) changes.push(moved);
  }
  return changes;
}

/**
 * «Сдвинуть проект на N рабочих дней»: каждая незакрытая задача с датами сдвигается на `delta` (обе даты —
 * `shiftWorkingDays`), закрытые и без дат остаются. Результат — только изменившиеся задачи.
 */
export function shiftProject(
  tasks: readonly DatedTask[],
  delta: number,
  calendar: WorkCalendar,
): DateChange[] {
  return tasks.flatMap((task) => {
    const moved = shifted(task, delta, calendar);
    return moved === null ? [] : [moved];
  });
}
